import { Button, MenuItem } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';

import { Chart } from 'components/charts/Chart';
import { ChartsGrid } from 'components/charts/ChartsGrid';
import { Donut } from 'components/charts/Donut';
import { TopDonutSection } from 'components/charts/TopDonutSection';
import { Section } from 'components/Section';
import { Dot } from 'components/table/Dot';
import { EeLoadingArea } from 'components/EeLoadingArea';
import { Menu } from 'components/Menu';
import { MenuLink } from 'components/Menu/MenuLink';
import { Table } from 'components/table/Table';

import { Header } from '../components/Header';
import AccessCaptableContent from '../components/AccessCaptableContent';
import { MetricsGrid } from '../components/MetricsGrid';
import { getValue } from '../dashboardSettingsUtils';

import { SETTINGS_MAP } from './constants';
import { FundSelector } from './FundSelector';
import { SubFundsPortfolio } from './SubFundsPortfolio';
import { useFund } from './useFund';
import { MappedComponent } from '../dashboardSettingsTypes';
import { LENS_COLORS_HEX, oldColors } from 'constants/colors';


export function Fund(props: { user: any | undefined }) {
  const logic = useFund();

  const isLensUI = Boolean(window.GLOBAL_CONFIG.FLIPPERS.LENS_UI)
  const colors = isLensUI ? LENS_COLORS_HEX : oldColors

  const { navigationPermissions = {} } = props.user || {}

  // overall variable across function 
  const fundAggregationCurrencyConfig = window.GLOBAL_CONFIG.CURRENCIES[logic?.fundAggregation?.currency || '']

  function mapNameToComponent(name: string, i: number) {
    if (!logic.fundAggregation || !logic.mappedSettings) return <div key={i} />;

    const section = logic.mappedSettings.sections.find(s => s.name === name);
    if (!section) throw new Error(`Section ${name} not found in mapped settings`);

    let components = section.components;
    if (logic.user?.role === 'LP') {
      if (section.name === 'keyMetricsCapital') {
        components = (
          [{
            label: 'Total Committed Capital',
            name: 'totalCommittedCapital',
            checked: true,
            order: 0,
            fieldName: 'totalCommittedCapital',
            fieldType: 'currency0',
            tooltip: 'Sum of LP Commitments to the funds',
          }] as MappedComponent[]
        ).concat(components.filter(c => ['capitalCalled', 'investedCapital', 'bookValue'].includes(c.name)));
      } else if (section.name === 'keyMetricsGrossAndNet') {
        components = (
          [{
            label: 'Multiple',
            name: 'multiple',
            checked: true,
            order: 0,
            fieldName: 'multiple',
            fieldType: 'ratio2',
            tooltip: 'Book Value divided by the Invested Capital',
          }] as MappedComponent[]
        ).concat(components.filter(c => ['multiple', 'grossIRR', 'netIRR'].includes(c.name)));
      }
    } else {
      components = components.filter(c => c.checked);
    }

    switch (name) {
      case 'keyMetricsCapital':
        return (
          <MetricsGrid
            title={`${logic.getAggregationVerboseName()} Key Metrics - Capital`}
            key={i}
            items={
              components
                .filter(c => (
                  logic.isSubfund
                    ? ['companiesNumber', 'investedCapital', 'realizedValue', 'bookValue'].includes(c.name)
                    : true
                ))
                .map(c => ({
                  ...c,
                  value: getValue(
                    c,
                    logic.fundAggregation,
                    'N/A',
                    fundAggregationCurrencyConfig
                  )
                }
                ))
            }
          />
        );
      case 'keyMetricsGrossAndNet':
        return (
          <MetricsGrid
            title={`${logic.getAggregationVerboseName()} Key Metrics - Gross and Net Metrics`}
            key={i}
            items={
              components
                .filter(c => (
                  logic.isSubfund
                    ? ['grossMultiple', 'grossRealizedMultiple', 'grossIRR'].includes(c.name)
                    : true
                ))
                .map(c => ({
                  ...c,
                  value: getValue(
                    c,
                    logic.fundAggregation,
                    'N/A',
                    fundAggregationCurrencyConfig
                  )
                }
                ))
            }
          />
        );
      case 'fundPerformance':
        if (logic.isSubfund) {
          return <TopDonutSection
            key={i}
            donutProps={{ title: '', data: [{ name: 'x', y: 1 }] }}
            sectionTitle="Fund Performance"
            columns={['SECTOR', 'INVESTMENT AMOUNT']}
            getRowCells={r => [r.name, `${fundAggregationCurrencyConfig?.sign} ${r.y}`]}
          />
        } else {
          return (
            <ChartsGrid
              key={i}
              title={`${logic.getAggregationVerboseName()} Performance`}
              charts={
                [
                  <Chart
                    title="Net Multiple"
                    config={{
                      chart: { type: 'area' },
                      colors,
                      series: [{
                        name: 'Net Multiple',
                        fillColor: 'rgba(150, 150, 150, .25)',
                        threshold: null,
                        data: logic.fundQuarters?.map(q => ({ name: q.asOfDate, y: q.multiple })) || []
                      }],
                      xAxis: {
                        type: 'category',
                        labels: {
                          formatter: function () { return ((this as any).value as moment.Moment).format('YYYY[Q]Q') }
                        },
                      },
                      yAxis: {
                        labels: { format: '{value:.1f}' },
                        title: { text: '' },
                        tickPixelInterval: 40,
                      },
                    }}
                  />,
                ].concat(
                  logic.fundAggregation.isSingleFund
                    ? [<Chart
                      title="DPI & RVPI"
                      config={{
                        chart: { type: 'column' },
                        plotOptions: {
                          column: { stacking: 'normal' },
                          series: {
                            pointPadding: .05,
                            groupPadding: .1,
                          }
                        },
                        colors,
                        tooltip: {
                          formatter: function (tooltip: any) {
                            (this as any).label = (this as any).key.format('YYYY[Q]Q');
                            return tooltip.defaultFormatter.call(this, tooltip);
                          },
                          headerFormat: '<b>{point.label}</b><br/>',
                          pointFormat: '{series.name}: {point.y:.2f}',
                        },
                        series: [{
                          name: 'RVPI',
                          data: logic.fundQuarters?.map(q => ({ name: q.asOfDate, y: q.rvpi })) || []
                        }, {
                          name: 'DPI',
                          data: logic.fundQuarters?.map(q => ({ name: q.asOfDate, y: q.dpi })) || []
                        }],
                        xAxis: {
                          type: 'category',
                          labels: {
                            formatter: function () {
                              return ((this as any).value as moment.Moment).format('YYYY[Q]Q')
                            }
                          },
                        },
                        yAxis: {
                          tickPixelInterval: 40,
                          title: { text: '' },
                          min: 0,
                        },
                      }}
                    />]
                    : []
                ).concat([
                  <Donut title="Investment by Sector" data={logic.fundAggregation.investmentsBySector} />,
                ]).concat(
                  logic.fundAggregation.isSingleFund
                    ? [
                      <Chart
                        title="Net IRR"
                        config={{
                          series: [{
                            name: 'Net IRR',
                            color: 'rgba(135, 96, 255)',
                            data: logic.fundQuarters?.map(q => ({ name: q.asOfDate, y: q.netIrr })) || []
                          }],
                          colors,
                          xAxis: {
                            type: 'category',
                            labels: {
                              formatter: function () { return ((this as any).value as moment.Moment).format('YYYY[Q]Q') }
                            },
                            gridLineWidth: 1,
                          },
                          yAxis: {
                            tickPixelInterval: 40,
                            title: { text: '' },
                          },
                        }}
                      />,
                    ]
                    : []
                )
              }
            />
          );
        }
      case 'subFundsPortfolio':
        if (
          logic.isSubfund
          || !logic.fundAggregation.funds.map(f => f.subfunds.length).reduce((a, b) => a + b, 0)
          || !logic.user
        ) return <div key={i} />;
        return <SubFundsPortfolio
          components={components}
          currencyConfig={fundAggregationCurrencyConfig}
          fundAggregation={logic.fundAggregation}
          key={i}
          user={logic.user!}
        />;

      case 'fundPortfolio':
        return <Table
          columns={[
            { label: 'Status', cellProps: { style: { width: '7%' } } },
            { label: 'Company', cellProps: { style: { width: '20%' } } },
            ...(['PM', 'GP'].includes(logic.user?.role || '')
              ? [{
                label: 'Cap Table',
                cellProps: { style: { width: '9%' }, align: 'center' as 'center' },
                nonSortable: true,
              }]
              : []),
            ...components.map(c => ({ label: c.label, tooltip: c.tooltip }))
          ]}
          key={i}
          rows={
            (() => {
              let result = [] as any[];
              logic.fundAggregation.funds.forEach((f) => {
                result = result.concat(f.fundInvestments.map(fi => [
                  ...[
                    {
                      content: fi.status === 'A_10'
                        ? <div style={{ display: 'flex', verticalAlign: 'middle' }}>
                          <Dot color="active" />Active
                        </div>
                        : <div style={{ display: 'flex', verticalAlign: 'middle' }}>
                          <Dot color="inactive" />Inactive
                        </div>,
                      sortingValue: fi.status,
                      cellProps: { style: { width: '7%' } },
                    },
                    {
                      content:
                        logic.user?.role === 'LP'
                          ? `${fi.company.name}${!logic.fundAggregation?.isSingleFund && ' - ' + fi.fund.name}`
                          : <a href={`/company/${fi.company.id}/?aggrId=${fi.fund.id}`}>
                            {fi.company.name}{!logic.fundAggregation?.isSingleFund && ' - ' + fi.fund.name}
                          </a>,
                      sortingValue: fi.company.name,
                      cellProps: { style: { width: '20%' } },
                    },
                  ],
                  ...(['PM', 'GP'].includes(logic.user?.role || '')
                    ? [
                      {
                        content:
                          <div style={{ width: '100%', textAlign: "center" }}>
                            <a href={fi.capTableUrl} title="Access Cap Table">
                              <img
                                className="svg-dark-invert"
                                src="/static/icons/icon_external_link.svg"
                                alt="Captable"
                              />
                            </a>
                          </div>,
                        cellProps: { style: { width: '9%' } },
                      }
                    ]
                    : []),
                  ...components.map(c => ({
                    content: getValue(
                      c,
                      fi,
                      '', fundAggregationCurrencyConfig,
                    ),
                    sortingValue: getValue(
                      c,
                      fi,
                      '', fundAggregationCurrencyConfig,
                      true,
                    ),
                  }))
                ]));
              });
              return result;
            })()
          }
          sectionTitle="Portfolio"
        />;

      case 'clonedCaptables':
        const clonedCaptables = logic?.fundAggregation?.clonedCaptables;
        return (
          clonedCaptables !== null && (
            <Table
              columns={[
                { label: 'Company Name' },
                { label: 'Clone Name' },
                {
                  label: 'Cap Table',
                  nonSortable: true,
                },
              ]}
              key={i}
              rows={(() => {
                let result = [] as any[];
                clonedCaptables?.forEach((c: any) => {
                  result.push([
                    {
                      content: c.clonedParentName,
                      sortingValue: c.clonedParentName,
                    },
                    {
                      content: c.clonedCapTableName,
                      sortingValue: c.clonedCapTableName,
                    },
                    {
                      content: (
                        <AccessCaptableContent
                          captableLink={c.clonedCapTableUrl}
                        />
                      ),
                    },
                  ]);
                });
                return result;
              })()}
              sectionTitle="Cloned Captables"
            />
          )
        );

      default:
        throw new Error(`Section ${name} is not implemented`)
    }
  }

  if (!logic.fundAggregation || !logic.user) {
    // If company is not loaded yet, show loading area
    // if there is no company found then it redirects to the admin funds page
    return (
      <div className="vh-100 d-flex justify-content-center align-items-center lens-h-80vh">
        <EeLoadingArea />
      </div>
    );
  }

  const lpInvestmentFunds = logic.user?.lpInvestments ? logic.user.lpInvestments.map(lp => lp.fund.id) : [];
  const headerItems = logic.funds.data
    ?.filter(f => !f.isSubfund)
    .filter(f => lpInvestmentFunds && lpInvestmentFunds.length ? lpInvestmentFunds.includes(f.id) : true);

  const title = logic.isSubfund
    ? <span>Sub Fund of <Link to={`/fund/${logic.fundAggregation.funds[0].parentFund?.id}`}>
      {logic.fundAggregation.funds[0].parentFund?.name}
    </Link></span>
    : logic.fundAggregation.name;

  const getDashboardSettingsMap = (logic: any) => {
    let mappedSettings = { ...SETTINGS_MAP };

    // the null conditions are handled on the backend
    if (logic?.fundAggregation?.clonedCaptables === null) {
      delete mappedSettings['clonedCaptables'];
    }

    return mappedSettings;
  };

  return (
    <>
      <Header
        confirmText={
          <>
            The changes you made will affect to all funds.
            <br /> Are you sure to apply?
          </>
        }
        dashboardSettings={logic.fundAggregation.dashboardSettings}
        description={
          <>
            Choose sections and components to be displayed in the Fund Dashboard.
            <br /> Changes will apply to all funds.
          </>
        }
        defaultDashboardSettings={logic.fundAggregation.defaultDashboardSettings}
        hideSettings={!logic.user || logic.user.role === 'LP'}
        onSettingsClose={logic.onSettingsClose}
        saveDashboardSettings={logic.saveDashboardSettings}
        settingsMap={getDashboardSettingsMap(logic)}
        submitText="APPLY FOR ALL FUNDS"
        title={title}
      />
      <Section>
        <FundSelector
          funds={headerItems}
          fundAggregation={logic.fundAggregation}
          fundIsLoading={logic.user.role === "LP" ? logic.fundLoadingLP : logic.fundLoading}
          reloadFund={logic.reloadFund}
        />
        {navigationPermissions.isGpOrPm && logic.fundAggregation.isSingleFund &&
          <>
            <Button
              href={`/admin/fund/${logic.fundAggregation.funds[0].id}/`}
              variant="outlined" style={{ float: 'right', marginLeft: 24 }}
            >
              Admin
            </Button>
            <Button
              aria-controls="export-menu"
              aria-haspopup="true"
              color="primary"
              endIcon={<ArrowDropDownIcon />}
              onClick={e => logic.setExportMenuAnchor(e.currentTarget)}
              style={{ float: 'right' }}
              variant="contained"
            >
              Export
            </Button>
            <Menu
              anchorEl={logic.exportMenuAnchor}
              id="export-menu"
              onClose={() => logic.setExportMenuAnchor(null)}
              open={!!logic.exportMenuAnchor}
              classes={{ paper: 'lens-dropdown-menu' }}
            >
              <MenuItem
                classes={{ root: 'lens-dropdown-list' }}
              >
                <MenuLink
                  to={`/fund/${logic.fundAggregation.funds[0].id}/status-report/?asofdate=${logic.fundAggregation.asOfDate.format('YYYYMMDD')}`}
                  target="_parent">
                  Status report
                </MenuLink>
              </MenuItem>
            </Menu>
          </>
        }
      </Section>

      {logic.fundLoading && <EeLoadingArea />}
      {logic.user?.role === 'LP' && logic.user?.profileLp &&
        <MetricsGrid
          title={'Your Account Performance'}
          items={[
            {
              label: 'Capital Contributed',
              value: getValue(
                { fieldName: 'contributedCapital', fieldType: 'currency0', label: '' },
                logic.user?.profileLp,
                '',
                fundAggregationCurrencyConfig,
              )
            },
            {
              label: 'Book Value',
              value: getValue(
                { fieldName: 'bookValue', fieldType: 'currency0', label: '' },
                logic.fundAggregation.currentLpInvestment,
                '',
                fundAggregationCurrencyConfig,
              )
            },
            {
              label: 'Return',
              value: getValue(
                { fieldName: 'returnPercent', fieldType: 'percentage2', label: '' },
                logic.user?.profileLp,
                '',
                fundAggregationCurrencyConfig,
              )
            },
          ]}
        />
      }
      {logic.fundAggregation.dashboardSettings.sections
        .filter(s => s.checked || logic.user?.role === 'LP')
        .map((section, i: number) => mapNameToComponent(section.name, i))
      }
    </>
  );
}